import clsx from 'clsx';
import { FC, useState } from 'react';
import { useIntl } from 'react-intl';

import { DraftState } from '../hooks/useDraft';
import { QuickSalePlanModel, RecipeModel } from '../models/QuickSaleModel';
import { formatReason } from '../utils/quickSaleFormat';

type Props = {
  petName: string;
  plan: QuickSalePlanModel;
  savedPlan: QuickSalePlanModel;
  draftState: DraftState;
  onEditCustom: () => void;
};

const FISH_RECIPE = /fish|salm|pesc/i;
// Enough to give the agent an argument without filling the card.
const MAX_REASONS = 3;

function recipeIcon(recipe: RecipeModel) {
  return FISH_RECIPE.test(recipe.name) ? 'fa-fish' : 'fa-drumstick-bite';
}

const RecipeRow: FC<{
  recipe: RecipeModel;
  tone: 'success' | 'danger' | 'muted';
  control?: React.ReactNode;
}> = ({ recipe, tone, control }) => {
  const intl = useIntl();
  const reasons = recipe.reasons.map((reason) => formatReason(intl, reason));

  return (
    <div className="d-flex align-items-start gap-4 py-3 border-bottom border-gray-300">
      {control}
      <i
        className={clsx(
          'fas fs-2 w-25px text-center',
          recipeIcon(recipe),
          tone === 'success' && 'text-warning',
          tone === 'danger' && 'text-danger',
          tone === 'muted' && 'text-gray-500'
        )}
      />
      <div className="fw-bolder text-gray-800 w-125px flex-shrink-0">
        {recipe.name}
      </div>
      <div
        className={clsx(
          'flex-grow-1',
          tone === 'danger' ? 'text-danger' : 'text-gray-700'
        )}
        title={reasons.join('\n')}
      >
        {reasons.slice(0, MAX_REASONS).join(' · ') || '-'}
      </div>
    </div>
  );
};

/**
 * Why each recipe is (or is not) in the plan, to be used as a sales argument,
 * and the recipes this pet cannot have and why.
 */
const RecipesCards: FC<Props> = ({
  petName,
  plan,
  savedPlan,
  draftState,
  onEditCustom,
}) => {
  const intl = useIntl();
  const [editing, setEditing] = useState(false);
  const { draft, update } = draftState;
  const recipes = plan.recipes;
  const savedSelected = savedPlan.recipes
    .filter((recipe) => recipe.status === 'selected')
    .map((recipe) => recipe.meal_id);
  const selectedIds =
    draft.plan?.meals ??
    recipes
      .filter((recipe) => recipe.status === 'selected')
      .map((recipe) => recipe.meal_id);
  const isAutomatic = (draft.plan?.kind ?? plan.kind) === 'automatic';

  const selected = recipes.filter((recipe) =>
    editing
      ? recipe.status !== 'not_allowed'
      : selectedIds.includes(recipe.meal_id)
  );
  const available = editing
    ? []
    : recipes.filter(
        (recipe) =>
          recipe.status === 'available' && !selectedIds.includes(recipe.meal_id)
      );
  const notAllowed = recipes.filter(
    (recipe) => recipe.status === 'not_allowed'
  );

  function toggle(mealId: number, checked: boolean) {
    const next = checked
      ? [...selectedIds, mealId]
      : selectedIds.filter((id) => id !== mealId);
    const unchanged =
      [...next].sort().join() === [...savedSelected].sort().join();
    update('plan', { meals: unchanged ? undefined : next });
  }

  function onToggleEdit() {
    if (!isAutomatic) {
      onEditCustom();
      return;
    }
    setEditing(!editing);
  }

  return (
    <div className="row g-5 mb-5">
      <div className="col-lg-6">
        <div className="card bg-light-warning h-100">
          <div className="card-header border-0 pt-5 min-h-auto">
            <h3 className="card-title align-items-center fw-bolder fs-4">
              <i className="fas fa-check-circle fs-2 me-3 text-success" />
              {intl.formatMessage(
                { id: 'quickSale.recommendedRecipes' },
                { name: petName }
              )}
            </h3>
            <div className="card-toolbar">
              <button
                type="button"
                className={clsx(
                  'btn btn-sm',
                  editing ? 'btn-primary' : 'btn-light'
                )}
                onClick={onToggleEdit}
              >
                <i className="fas fa-pen fs-7 me-1" />
                {intl.formatMessage({
                  id: editing ? 'quickSale.done' : 'quickSale.edit',
                })}
              </button>
            </div>
          </div>
          <div className="card-body pt-2">
            {selected.map((recipe) => (
              <RecipeRow
                key={recipe.meal_id}
                recipe={recipe}
                tone="success"
                control={
                  editing && (
                    <input
                      type="checkbox"
                      className="form-check-input mt-1"
                      aria-label={recipe.name}
                      checked={selectedIds.includes(recipe.meal_id)}
                      onChange={(event) =>
                        toggle(recipe.meal_id, event.target.checked)
                      }
                    />
                  )
                }
              />
            ))}
            {!selected.length && (
              <div className="text-muted py-3">
                {intl.formatMessage({ id: 'quickSale.noRecipes' })}
              </div>
            )}
            {!!available.length && (
              <>
                <div className="fw-bold text-gray-600 mt-4">
                  {intl.formatMessage({ id: 'quickSale.alsoOffer' })}
                </div>
                {available.map((recipe) => (
                  <RecipeRow
                    key={recipe.meal_id}
                    recipe={recipe}
                    tone="muted"
                  />
                ))}
              </>
            )}
          </div>
        </div>
      </div>
      <div className="col-lg-6">
        <div className="card bg-light-danger h-100">
          <div className="card-header border-0 pt-5 min-h-auto">
            <h3 className="card-title align-items-center fw-bolder fs-4 text-danger">
              <i className="fas fa-ban fs-2 me-3 text-danger" />
              {intl.formatMessage({ id: 'quickSale.notRecommendedRecipes' })}
            </h3>
          </div>
          <div className="card-body pt-2">
            {notAllowed.map((recipe) => (
              <RecipeRow key={recipe.meal_id} recipe={recipe} tone="danger" />
            ))}
            {!notAllowed.length && (
              <div className="text-muted py-3">
                {intl.formatMessage({ id: 'quickSale.allRecipesAllowed' })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export { RecipesCards };
